/**
 * Footer component for the animation platform
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    platform: [
      { name: 'Home', path: '/' },
      { name: 'Browse Content', path: '/browse' },
      { name: 'How It Works', path: '/how-it-works' },
    ],
    support: [
      { name: 'Help Center', path: '/help' },
      { name: 'Contact Us', path: '/contact' },
      { name: 'FAQ', path: '/faq' },
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
      { name: 'Cookie Policy', path: '/cookies' },
    ],
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Main footer content - 2 columns on mobile, 4 on desktop */}
        <div className="py-6 sm:py-12 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-8">
          {/* Brand section - Full width on mobile, spans 2 cols on desktop */}
          <div className="col-span-2 lg:col-span-2 mb-4 sm:mb-0">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg sm:text-xl">🦕</span>
              </div>
              <div>
                <h3 className="text-base sm:text-xl font-bold text-white">Dino Learn</h3>
                <p className="text-xs sm:text-sm text-gray-400">Interactive Learning Platform</p>
              </div>
            </div>
            
            {/* Hide description on mobile, show on tablet+ */}
            <p className="hidden sm:block text-gray-400 mb-6 max-w-md text-sm">
              Making learning engaging and fun through interactive animations. 
              Designed for students, loved by teachers, trusted by parents.
            </p>
            
            {/* Contact info - Compact on Mobile */}
            <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
              <div className="flex items-center space-x-2">
                <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-primary-400" />
                <a href="mailto:hello@dinolabs.com" className="hover:text-white transition-colors">
                  hello@dinolabs.com
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-primary-400" />
                <a href="tel:+918978278117" className="hover:text-white transition-colors">
                  +91 89782 78117
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-primary-400" />
                <span>Hyderabad, India</span>
              </div>
            </div>
          </div>

          {/* Platform links - Compact on Mobile, side-by-side with Support */}
          <div>
            <h4 className="text-white font-semibold mb-1.5 sm:mb-4 text-xs sm:text-base">Platform</h4>
            <ul className="space-y-1 sm:space-y-2">
              {footerLinks.platform.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-xs sm:text-sm hover:text-white transition-colors block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support links - Compact on Mobile, side-by-side with Platform */}
          <div>
            <h4 className="text-white font-semibold mb-1.5 sm:mb-4 text-xs sm:text-base">Support</h4>
            <ul className="space-y-1 sm:space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-xs sm:text-sm hover:text-white transition-colors block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom section - Compact on Mobile */}
        <div className="py-3 sm:py-6 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            {/* Copyright - Smaller on Mobile */}
            <div className="text-xs sm:text-sm text-gray-400 text-center md:text-left">
              © {currentYear} Dino Learn. All rights reserved.
            </div>

            {/* Legal links - Compact on Mobile */}
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
              {footerLinks.legal.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="text-xs sm:text-sm text-gray-400 hover:text-white transition-colors whitespace-nowrap"
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Made with love - Hidden on mobile, show on tablet+ */}
            <div className="hidden md:flex items-center space-x-1 text-sm text-gray-400">
              <span>Made with</span>
              <Heart className="w-4 h-4 text-red-500" />
              <span>for learners</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
